import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';

import { UserDto } from '@core/users/user.model';

import { ContactDto } from '@core/contacts/contacts.model';
import { AdditionalFolderDetailDto, ProcessType } from '@apps/file-process/process/process.model';
import { toFormData } from '@platform/shared/utilities/file-utility';
import { CustomerSpaceAdminDto, CustomerSpaceDto, PostCustomerSpaceDto, PostCustomerSpaceUserDto } from './customer-space.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerSpaceService {
  constructor(private http: HttpClient) {}

  public loadCustomerSpace(customerSpaceId: string): Observable<CustomerSpaceDto> {
    return this.http.get<CustomerSpaceDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}`);
  }

  public loadCustomerSpaces(): Observable<CustomerSpaceDto[]> {
    return this.http.get<CustomerSpaceDto[]>(`${environment.apiUrl}/customer-spaces`);
  }

  public getCustomerSpaceCount(): Observable<number> {
    return this.http.get<number>(`${environment.apiUrl}/customer-spaces/count`);
  }

  public loadCustomerSpaceAdmin(customerSpaceId: string): Observable<CustomerSpaceAdminDto> {
    return this.http.get<CustomerSpaceAdminDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/admin`);
  }

  public createCustomerSpace(customerSpace: PostCustomerSpaceDto): Observable<CustomerSpaceAdminDto> {
    return this.http.post<CustomerSpaceAdminDto>(`${environment.apiUrl}/customer-spaces`, customerSpace);
  }

  public updateCustomerSpace(customerSpaceId: string, customerSpace: PostCustomerSpaceDto): Observable<CustomerSpaceAdminDto> {
    return this.http.put<CustomerSpaceAdminDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}`, customerSpace);
  }

  public deleteCustomerSpace(customerSpaceId: string): Observable<string> {
    return this.http.delete<string>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}`);
  }

  public removeCustomerSpaceUser(customerSpaceId: string, userId: string): Observable<CustomerSpaceAdminDto> {
    return this.http.delete<CustomerSpaceAdminDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/users/${userId}`);
  }

  public updateCustomerSpaceUser(
    customerSpaceId: string,
    postCustomerSpaceUser: PostCustomerSpaceUserDto,
  ): Observable<CustomerSpaceAdminDto> {
    return this.http.post<CustomerSpaceAdminDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/users`, postCustomerSpaceUser);
  }

  public loadCustomerSpaceUsers(customerSpaceId: string): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/users`);
  }

  public loadCustomerSpaceContacts(customerSpaceId: string): Observable<ContactDto[]> {
    return this.http.get<ContactDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/contacts`);
  }

  public isCurrentUserCustomerSpaceAdmin(customerSpaceId: string): Observable<boolean> {
    return this.http.get<boolean>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/isCurrentUserCustomerSpaceAdmin`);
  }

  public extractCustomerSpaceSharepointTemplate(): Observable<string> {
    return this.http.post<string>(`${environment.apiUrl}/customer-spaces/extract-template`, null);
  }

  public uploadBatchCreationFiles(files: FileList | File[]): Observable<any> {
    const formData = toFormData(files);
    return this.http.post(`${environment.apiUrl}/customer-spaces/batch-creation/upload`, formData);
  }
}
